/** @jsx jsx */

/** @jsxFrag React.Fragment */
import type { SerializedStyles } from '@emotion/react';
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { ChatMessage as CdsLabsChatMessage } from '@coursera-web/cds-labs';

import { AIGenerateBrandedNewIcon } from '@coursera/cds-icons';
import { useTracker } from '@coursera/event-pulse/react';

import type { FeedbackIssueOption } from 'bundles/ai-coach-platform/components/patterns/chat/FeedbackModal';
import ChatMessageBody from 'bundles/ai-coach-platform/components/patterns/chat/common/messages/ChatMessageBody';
import ChatMessageFooter from 'bundles/ai-coach-platform/components/patterns/chat/common/messages/ChatMessageFooter';
import ChatMessageHeader from 'bundles/ai-coach-platform/components/patterns/chat/common/messages/ChatMessageHeader';
import { brandUpdateCoachMessageStyles } from 'bundles/ai-coach-platform/components/patterns/chat/common/messages/brandUpdateCoachMessageStyles';
import { useCoachChatContext } from 'bundles/ai-coach-platform/components/patterns/chat/context/CoachChatContext';
import useFeedback from 'bundles/ai-coach-platform/components/patterns/chat/hooks/useFeedback';
import ChatRecommendations from 'bundles/ai-coach-platform/components/patterns/chat/recommendations/ChatRecommendations';
import type { MessageTypes } from 'bundles/ai-coach-platform/components/patterns/chat/store/types';
import type { CoachPersona, SendMessageFunc } from 'bundles/ai-coach-platform/components/patterns/chat/types';
import {
  isCoachMessage as isCoachMessageUtil,
  isErrorMessage as isErrorMessageUtil,
  isTextMessage as isTextMessageUtil,
} from 'bundles/ai-coach-platform/components/patterns/chat/utils/messageUtils';
import { useCdsLabsChatComponents } from 'bundles/ai-coach-platform/utils/statsigFeatureFlags';
import { useCoachTrackingContext } from 'bundles/ai-coach-platform/utils/tracking';

export type Props = React.PropsWithChildren<{
  /**
   * current chat message
   */
  message: MessageTypes;
  /**
   * zero-based index of the current message
   */
  index: number;
  /**
   * all chat messages in store currently
   */
  messages: MessageTypes[];
  sendMessage: SendMessageFunc;
  learnerPersona: CoachPersona;
  coachPersona: CoachPersona;

  hideRecommendedActions?: boolean;
  searchTerm?: string;
  recommendedActionsLabel?: string;
  onRecommendedActionClick?: () => void;
  /**
   * Optional caller-supplied styles applied on top of recommended action button's default styles.
   * Temporary prop for the Two-Panel XDP experiment (MERCH-2963). Please consult the platform team before using it in your project.
   */
  recommendedActionButtonCustomStyles?: SerializedStyles;
  feedbackIssueOptions?: FeedbackIssueOption[];
  onFeedbackSubmitted?: (args: { messageId: string; userIssue?: string; userComments: string }) => void;
}>;

const styles = {
  footer: css`
    justify-content: end;
  `,
};

const ChatMessage = ({
  message,
  index,
  messages,
  sendMessage,
  coachPersona,
  learnerPersona,
  children,
  hideRecommendedActions,
  searchTerm,
  recommendedActionsLabel,
  onRecommendedActionClick,
  recommendedActionButtonCustomStyles,
  feedbackIssueOptions,
  onFeedbackSubmitted,
}: Props) => {
  const { sender } = message;

  const { isBrandUpdateEnabled } = useCoachChatContext();
  const isCdsLabsEnabled = useCdsLabsChatComponents();

  const isLastMessage = index === messages.length - 1;
  const isCoachMessage = isCoachMessageUtil(sender);
  const persona = isCoachMessage ? coachPersona : learnerPersona;

  const showCopyMessage = isCoachMessage && isTextMessageUtil(message as MessageTypes);
  const isErrorMessage = isCoachMessage && isErrorMessageUtil(message as MessageTypes);

  // useFeedback must be called unconditionally (hook rules); results used only in the gate-on path
  const { onThumbsUp, onThumbsDown, thumbsUpSelected, thumbsDownSelected, feedbackModal } = useFeedback({
    message,
    feedbackIssueOptions,
    onFeedbackSubmitted,
  });

  const track = useTracker();
  const trackingContext = useCoachTrackingContext()?.data;

  const handleCopy = React.useCallback(() => {
    if (!trackingContext) return;
    track('interact_coach', {
      coachInteractionType: 'copy_coach_message',
      coach: { mode: trackingContext.mode },
      ...(trackingContext.courseId && { product: { type: 'course', id: trackingContext.courseId as string } }),
      ...(trackingContext.itemId && {
        courseItem: {
          id: trackingContext.itemId as string,
          ...(trackingContext.itemType && { type: trackingContext.itemType }),
        },
      }),
    });
  }, [track, trackingContext]);

  const recommendations =
    isCoachMessage && !hideRecommendedActions ? (
      <ChatRecommendations
        message={message}
        sendMessage={sendMessage}
        showRecommendedActions={isLastMessage}
        recommendedActionsLabel={recommendedActionsLabel}
        onRecommendedActionClick={onRecommendedActionClick}
        recommendedActionButtonCustomStyles={recommendedActionButtonCustomStyles}
      />
    ) : null;

  if (isCdsLabsEnabled) {
    if (isCoachMessage) {
      return (
        <CdsLabsChatMessage
          variant="llm"
          content={
            <ChatMessageBody message={message} searchTerm={searchTerm}>
              {recommendations}
              {children}
            </ChatMessageBody>
          }
          isLatestMessage={isLastMessage}
          actionButtons={
            !isErrorMessage
              ? {
                  showCopy: showCopyMessage,
                  onCopy: handleCopy,
                  onThumbsUp,
                  thumbsUpSelected,
                  onThumbsDown,
                  thumbsDownSelected,
                  extraActions: feedbackModal,
                }
              : undefined
          }
        />
      );
    }

    return <CdsLabsChatMessage variant="user" content={message.text} />;
  }

  // Gate off: brand-update coach layout
  if (isBrandUpdateEnabled && isCoachMessage) {
    return (
      <div css={brandUpdateCoachMessageStyles.layout}>
        <div css={brandUpdateCoachMessageStyles.sparkleIconContainer}>
          <AIGenerateBrandedNewIcon size="medium" />
        </div>
        <div css={brandUpdateCoachMessageStyles.content}>
          <ChatMessageBody message={message} searchTerm={searchTerm}>
            {recommendations}
            {children}
          </ChatMessageBody>
          {!isErrorMessage && (
            <ChatMessageFooter
              showCopyMessage={showCopyMessage}
              css={styles.footer}
              message={message}
              feedbackIssueOptions={feedbackIssueOptions}
              onFeedbackSubmitted={onFeedbackSubmitted}
            />
          )}
        </div>
      </div>
    );
  }

  // Gate off: legacy path for all other messages
  return (
    <>
      <ChatMessageHeader senderName={persona.name} senderAvatar={persona.avatar} />

      <ChatMessageBody message={message} className="coach-chat-message-body" searchTerm={searchTerm}>
        {isCoachMessage && !hideRecommendedActions ? (
          <ChatRecommendations
            message={message}
            sendMessage={sendMessage}
            showRecommendedActions={isLastMessage}
            recommendedActionsLabel={recommendedActionsLabel}
            onRecommendedActionClick={onRecommendedActionClick}
            recommendedActionButtonCustomStyles={recommendedActionButtonCustomStyles}
          />
        ) : null}
        {children}
      </ChatMessageBody>

      {isCoachMessage && !isErrorMessage && (
        <ChatMessageFooter showCopyMessage={showCopyMessage} css={styles.footer} message={message} />
      )}
    </>
  );
};

export default ChatMessage;
